import React from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import FloatingChatWidget from './FloatingChatWidget';
import ClientFloatingChatWidget from './ClientFloatingChatWidget';
import { hasMinimumRole } from '../../utils/permissions';

function ConditionalChatWidget({
  user,
  createWorkoutModalVisible,
  aiGenerateModalVisible,
}) {
  const location = useLocation();

  // Don't render chat widget if user is not authenticated
  if (!user) return null;

  // Suppress the floating bubble when the user is already on the
  // full Messages page — both the page and the bubble would render
  // the same conversation thread + Broadcasts tab side-by-side,
  // which is redundant and visually noisy. The bubble's purpose is
  // quick chat access from anywhere ELSE in the app.
  if (location?.pathname?.startsWith('/dashboard/messages')) {
    return null;
  }

  // The challenge landing-page builder is a focused, full-width editor with its
  // own live preview — the floating bubble overlaps its controls and clutters
  // the canvas. Hide it there.
  if (/^\/dashboard\/admin\/challenges\/[^/]+\/landing/.test(location?.pathname || '')) {
    return null;
  }

  // Hide the bubble while the workout-create/log modal OR the AI
  // workout-generator modal is open. Both eat most of the viewport
  // and the bubble's floating position overlaps their primary action
  // buttons; the trainer/client wants focus on the task, not chat.
  // Reopens automatically when either modal closes.
  if (createWorkoutModalVisible || aiGenerateModalVisible) {
    return null;
  }

  // Show admin chat widget for trainers and above (both manage clients)
  // This includes: trainer, organization_admin, platform_admin
  if (hasMinimumRole(user.role, 'trainer')) {
    return <FloatingChatWidget />;
  }

  // Show client chat widget for regular users
  if (user.role === 'client') {
    return <ClientFloatingChatWidget />;
  }

  // Don't render for other roles or unauthenticated users
  return null;
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  // Same redux flags the modals themselves read to toggle visibility —
  // see ducks/Global/reducer.js for `createWorkoutModalVisible` and
  // `aiGenerateModalVisible`.
  createWorkoutModalVisible: state.global.createWorkoutModalVisible,
  aiGenerateModalVisible: state.global.aiGenerateModalVisible,
});

export default connect(mapStateToProps)(ConditionalChatWidget);
